import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

// A horizontal, one-slide-at-a-time photo track built on CSS scroll-snap.
//
// Scroll-snap rather than a carousel library, and the reason is not only the
// ~10 KB: on a scroll container the swipe, the momentum, the rubber-banding at
// the ends and the accessibility of a scrollable region are the platform's,
// already correct on every phone, and identical to what a guest's thumb
// expects everywhere else on their device. A JS gesture engine re-implements
// all of that and gets to be subtly wrong at it. What is left for us is the
// part the platform does NOT give: knowing which slide is showing, and moving
// to one on demand.
//
// The index comes from scroll position rather than from an IntersectionObserver
// because it must be right MID-gesture (the counter updates as the guest
// drags), and because rounding a ratio is exact here — every slide is the full
// width of the viewport.

export interface PhotoTrackHandle {
  index: number;
  count: number;
  goTo: (i: number) => void;
  next: () => void;
  prev: () => void;
  trackRef: React.RefObject<HTMLDivElement | null>;
  onScroll: () => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
}

// prefersReducedMotion is read at call time, not cached: a guest can change the
// OS setting with the page open, and honouring it only until the next reload
// is not honouring it.
function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

export function usePhotoTrack(count: number, initialIndex = 0): PhotoTrackHandle {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const [index, setIndex] = useState(initialIndex);

  // While a programmatic move is in flight the track passes THROUGH the
  // intermediate offsets, and a scroll event at one of them rounds to the slide
  // being passed rather than the one being travelled to — which is a counter
  // that reads "1 / 10" a beat after the guest asked for the second photo, and
  // sometimes stays there. So during a move the index follows the INTENT, and
  // scroll-derived updates are ignored until the track arrives.
  const intentRef = useRef<number | null>(initialIndex === 0 ? null : initialIndex);
  const intentTimer = useRef<number | null>(null);

  // clearIntentSoon is the escape hatch: a move that never arrives — the guest
  // grabbed the track mid-animation, the browser dropped the scroll — must not
  // freeze the counter for the rest of the session.
  const armIntent = useCallback((target: number | null) => {
    intentRef.current = target;
    if (intentTimer.current !== null) window.clearTimeout(intentTimer.current);
    if (target === null) return;
    intentTimer.current = window.setTimeout(() => {
      intentRef.current = null;
      intentTimer.current = null;
    }, 1000);
  }, []);

  useEffect(
    () => () => {
      if (intentTimer.current !== null) window.clearTimeout(intentTimer.current);
    },
    [],
  );

  const goTo = useCallback(
    (i: number) => {
      const el = trackRef.current;
      const clamped = Math.max(0, Math.min(i, count - 1));
      setIndex(clamped);
      armIntent(clamped);
      if (!el) return;
      // scrollTo is guarded because jsdom does not implement it — a unit test
      // that drives the arrows should assert the index, not crash on layout.
      if (typeof el.scrollTo !== 'function') return;
      el.scrollTo({
        left: clamped * el.clientWidth,
        behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      });
    },
    [armIntent, count],
  );

  const onScroll = useCallback(() => {
    const el = trackRef.current;
    if (!el || el.clientWidth === 0) return;
    const at = Math.max(0, Math.min(Math.round(el.scrollLeft / el.clientWidth), count - 1));

    const intent = intentRef.current;
    if (intent !== null) {
      // Still travelling. Every offset on the way there is a lie about which
      // photo the guest is looking at.
      if (at !== intent) return;
      armIntent(null);
    }
    setIndex((prev) => (prev === at ? prev : at));
  }, [armIntent, count]);

  const next = useCallback(() => goTo(index + 1), [goTo, index]);
  const prev = useCallback(() => goTo(index - 1), [goTo, index]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      // preventDefault because a scroll container answers arrow keys with a
      // pixel nudge, which lands the guest between two photos.
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        next();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        prev();
      }
    },
    [next, prev],
  );

  // Open at initialIndex.
  //
  // Not `scrollTo(initialIndex * clientWidth)` in a plain effect: on mount the
  // track is in the DOM but not laid out, clientWidth is 0, and that
  // arithmetic scrolls to 0. Not `scrollIntoView` either — it is a no-op on a
  // zero-width box, and whether layout has happened by the first effect is a
  // race that a fast machine wins and a CI runner does not. So: try, CHECK,
  // and try again next frame until the track actually holds the position.
  //
  // scrollLeft rather than scrollTo: no smooth behaviour to wait on, and it is
  // a plain property, so there is no API to feature-detect.
  useLayoutEffect(() => {
    if (initialIndex === 0) return;
    let frames = 0;
    let raf = 0;
    const settle = () => {
      const el = trackRef.current;
      if (!el) return;
      const target = initialIndex * el.clientWidth;
      if (el.clientWidth > 0) {
        el.scrollLeft = target;
        if (Math.abs(el.scrollLeft - target) < 2) return;
      }
      // Ten frames is a sixth of a second — far past first layout on anything.
      // Giving up leaves the intent armed; its own timer releases it.
      if (++frames < 10) raf = requestAnimationFrame(settle);
    };
    settle();
    return () => cancelAnimationFrame(raf);
  }, [initialIndex]);

  // A shrinking count (an apartment whose photos changed under an open view)
  // must not leave the index pointing past the end.
  useEffect(() => {
    setIndex((prev) => Math.max(0, Math.min(prev, count - 1)));
  }, [count]);

  return { index, count, goTo, next, prev, trackRef, onScroll, onKeyDown };
}
